import InboxRuleFolderType from '../../store/schema/InboxRuleFolderType';
import { isFeatureEnabled } from 'owa-feature-flags';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { getPrimaryMailboxRootFolder, getFolderTable, Folder } from 'owa-folders';

// Same order as in OWA:MailFolderTreeNodeViewModel.cs
const FOLDERS_TO_SHOW_FIRST: DistinguishedFolderIdName[] = [
    'inbox',
    'junkemail',
    'drafts',
    'sentitems',
    'scheduled',
    'deleteditems',
];
const GEEK_FOLDERS_TO_HIDE: DistinguishedFolderIdName[] = ['journal', 'outbox', 'syncissues'];
const FOLDER_DELIMITER = '\\';

// returns the flat folder tree to be used in the move to dropdown
export default function getContextMenuFolders(): InboxRuleFolderType[] {
    // Get flat folder tree
    let folderList: InboxRuleFolderType[] = [];

    // First get the first level of the tree in order, with special folders first
    let childFolderIds = getFirstLevelFoldersInOrder();

    // recursively add all of the child folders of the root
    addAllChildFolders(folderList, childFolderIds, '');
    return folderList;
}

function getFirstLevelFoldersInOrder() {
    let childFolderIds: string[] = [];
    const rootFolder = getPrimaryMailboxRootFolder();

    // First add all the special folders.
    FOLDERS_TO_SHOW_FIRST.forEach(distinguishedFolderId => {
        let folder = getFolderTable().get(folderNameToId(distinguishedFolderId));
        if (folder && shouldDisplayFolderInFolderTree(folder)) {
            childFolderIds.push(folder.FolderId.Id);
        }
    });

    // Add all the remaining folders that are not part of the special list above.
    if (rootFolder && rootFolder.childFolderIds) {
        rootFolder.childFolderIds.map(childId => {
            let folder = getFolderTable().get(childId);
            if (
                folder &&
                FOLDERS_TO_SHOW_FIRST.indexOf(folderIdToName(folder.FolderId.Id)) == -1 &&
                shouldDisplayFolderInFolderTree(folder)
            ) {
                childFolderIds.push(folder.FolderId.Id);
            }
        });
    }

    return childFolderIds;
}

// Recursive function to add all child folders in tree for dropdown list
function addAllChildFolders(
    folderList: InboxRuleFolderType[],
    childFolders: string[],
    currentPath: string
) {
    for (let childFolderId of childFolders) {
        let childFolder = getFolderTable().get(childFolderId);
        // Don't add hidden folders or special folders(they are already added) to list
        if (shouldDisplayFolderInFolderTree(childFolder)) {
            let childFolderPath = currentPath + childFolder.DisplayName;
            const parentFolder = getFolderTable().get(childFolder.ParentFolderId.Id);
            const parentFolderDisplayName = parentFolder ? parentFolder.DisplayName : '';
            let childFolderRuleType = createRuleFolderFromFolderType(
                childFolder,
                childFolderPath,
                parentFolderDisplayName
            );

            folderList.push(childFolderRuleType);
            if (childFolder.childFolderIds) {
                addAllChildFolders(
                    folderList,
                    childFolder.childFolderIds,
                    childFolderPath + FOLDER_DELIMITER
                );
            }
        }
    }
}

function isOfTypeMailFolder(folder: Folder): boolean {
    return folder && folder.__type == 'Folder:#Exchange';
}

function shouldDisplayFolderInFolderTree(folder: Folder): boolean {
    // Do not show the folder if it's not a mail folder.
    if (!isOfTypeMailFolder(folder)) {
        return false;
    }

    const folderName = folderIdToName(folder.FolderId.Id);

    // Suppress hidden and 'geek' folders (outbox, journal, etc).
    if (folder.isHidden || GEEK_FOLDERS_TO_HIDE.indexOf(folderName) >= 0) {
        return false;
    }

    // Only show the Notes folder if that feature is enabled.
    let userConfiguration = getUserConfiguration();
    if (folderName == 'notes') {
        return (
            userConfiguration.SegmentationSettings.StickyNotes &&
            isFeatureEnabled('cal-stickyNotesFolder')
        );
    }

    // Only show the Junk Email folder if that feature is enabled.
    if (folderName == 'junkemail' && !userConfiguration.SegmentationSettings.JunkEMail) {
        return false;
    }

    // Good folder!
    return true;
}

function createRuleFolderFromFolderType(
    folderToConvert: Folder,
    currentPath: string,
    parentFolderDisplayName: string
): InboxRuleFolderType {
    return {
        folderId: folderToConvert.FolderId.Id,
        displayName: folderToConvert.DisplayName,
        parentFolderId: folderToConvert.ParentFolderId.Id,
        parentFolderDisplayName: parentFolderDisplayName,
        childFolderIds: folderToConvert.childFolderIds,
        folderType: folderToConvert.__type,
        isHidden: folderToConvert.isHidden,
        path: currentPath,
    };
}
